import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from "@angular/core";
import { IMenuOption, IMenuSelectEventData } from "@tylertech/forge";
import { DialogService, ForgeIconButtonModule, ForgeIconModule, ForgeMenuModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { WINDOW } from "@tylertech/shared-module";
import { SystemInfoExtended, SystemInfoService, WalkMeService } from "../../../services";
import { ShortCutLegendComponent } from "../shortcutLegend";
import { SUPPORT_PORTAL_URL, productSupportUrl } from "../supportLinks";

@Component({
    selector: "help-menu",
    templateUrl: "./helpMenu.tmpl.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeMenuModule,
        ForgeTooltipModule,
    ]
})
export class HelpMenuComponent
{
    cdr = inject(ChangeDetectorRef);
    private readonly window = inject<globalThis.Window>(WINDOW);
    private readonly forgeDialogService = inject(DialogService);
    private readonly $systemInfoService = inject(SystemInfoService);
    private readonly $walkMeService = inject(WalkMeService);

    private supportPortal = SUPPORT_PORTAL_URL;
    private tylerSearchUrl = "https://tylertech.microsoftcrmportals.com/search/#t=All&sort=relevancy";
    private tylerCommunityUrl = "https://tylercommunity.tylertech.com/";
    private tylerUniversity = "https://tylertech.csod.com/samldefault.aspx";
    helpOptions: Array<IMenuOption> = [];
    learnMoreUrl: string;
    loaded: boolean = false;

    constructor()
    {
        this.$systemInfoService.GetCachedSystemInfo()
            .then((result: SystemInfoExtended) =>
            {
                this.learnMoreUrl = productSupportUrl(result.Product);
                this.setHelpOptions();
            });
    }

    setHelpOptions(): void
    {
        this.helpOptions.push({
            value: "support-portal",
            label: "Bomgar",
            icon: "contact_phone"
        });
        if (this.$walkMeService.Enabled)
        {
            this.helpOptions.push({
                value: "walk-me-descriptions",
                label: "Field Descriptions",
                icon: "info_outline",
                leadingIconType: "component"
            });
            this.helpOptions.push({
                value: "walk-me",
                label: "Tyler Coach",
                icon: "tyler_coach_account_info",
                leadingIconType: "component"
            });
        }

        this.helpOptions.push({
            value: "tyler-search",
            label: "Tyler Search",
            icon: "search",
        });
        this.helpOptions.push({
            value: "tyler-community",
            label: "Tyler Community",
            icon: "people",
        });
        this.helpOptions.push({
            value: "tyler-university",
            label: "Tyler University",
            icon: "school",
            leadingIconType: "component"
        });
        this.helpOptions.push({
            value: "tyler-learn-more",
            label: "Learn about Help",
            icon: "help_box",
            leadingIconType: "component"
        });
        this.helpOptions.push({
            value: "shortcuts",
            label: "Keyboard Shortcuts",
            icon: "keyboard"
        });
        this.loaded = true;
        this.cdr.detectChanges();
    }

    menuOpened(e): void
    {
        e.target.activateFirstOption();
    }

    itemSelected($event: Event): void
    {

        const detail = ($event as CustomEvent<IMenuSelectEventData>).detail;
        switch (detail.value)
        {
            case "walk-me":
                this.$walkMeService.toggleWalkMe();
                break;
            case "walk-me-descriptions":
                this.$walkMeService.toggleWalkMePageDescriptions();
                break;
            case "tyler-search":
                this.open(this.tylerSearchUrl);
                break;
            case "tyler-community":
                this.open(this.tylerCommunityUrl);
                break;
            case "tyler-university":
                this.open(this.tylerUniversity);
                break;
            case "tyler-learn-more":
                this.open(this.learnMoreUrl);
                break;
            case "support-portal":
                this.open(this.supportPortal);
                break;
            case "shortcuts":
                this.forgeDialogService.open(ShortCutLegendComponent);
                break;
            default:
                break;
        }
    }

    private open(url: string): void
    {
        this.window.open(url, "_blank");
    }
}
